import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateFunction, useFunctions } from '../../../platform/api/hooks';
import { asList, asRecord, str, getMeta } from '../../../platform/api/utils';
import { labelOf } from '../../../shared/labels';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';

const TYPE_LABELS: Record<string, string> = {
  RELIGIOUS: 'Religioso',
  ADMINISTRATIVE: 'Administrativo',
  OPERATIONAL: 'Operacional',
  SUPPORT: 'Apoio',
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  ARCHIVED: 'Arquivado',
};

export function FunctionsList() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useFunctions();
  const createFn = useCreateFunction();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('OPERATIONAL');
  const [description, setDescription] = useState('');
  const [isSensitive, setIsSensitive] = useState(false);

  if (isError) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1000 }}>
        <PageHeader title="Cargos e funções" subtitle="Funções institucionais da casa" />
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', margin: 0 }}>Não foi possível carregar os cargos.</p>
        </Section>
      </div>
    );
  }

  const list = asList(data);
  const meta = getMeta(data);

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1000 }}>
      <PageHeader
        title="Cargos e funções"
        subtitle={`${str(meta.total, String(list.length))} funções institucionais`}
        action={{
          label: '+ Novo cargo',
          onClick: () => {
            setShowForm(true);
          },
        }}
      />

      <Section title="Importante">
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--muted)', lineHeight: 1.5 }}>
          Cargos e funções descrevem o papel da pessoa na casa (religioso, administrativo ou
          operacional). <strong>Eles não concedem acesso digital automaticamente.</strong> Para
          liberar o sistema, use <em>Permissões de acesso</em>.
        </p>
      </Section>

      {isLoading && <p style={{ color: 'var(--faint)' }}>Carregando...</p>}

      {!isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
          {showForm && (
            <Section title="Novo cargo ou função">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                  placeholder="Nome (ex.: Zelador(a))"
                  style={inputCss}
                />
                <select
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                  }}
                  style={inputCss}
                >
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
                <input
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                  }}
                  placeholder="Descrição (opcional)"
                  style={inputCss}
                />
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}
                >
                  <input
                    type="checkbox"
                    checked={isSensitive}
                    onChange={(e) => {
                      setIsSensitive(e.target.checked);
                    }}
                  />
                  Função sensível
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                    }}
                    style={secondaryBtn}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={!name.trim() || createFn.isPending}
                    onClick={() => {
                      createFn.mutate({
                        name: name.trim(),
                        type,
                        description: description.trim() || undefined,
                        isSensitive,
                      });
                      setName('');
                      setDescription('');
                      setIsSensitive(false);
                      setShowForm(false);
                    }}
                    style={primaryBtn}
                  >
                    {createFn.isPending ? 'Salvando...' : 'Criar'}
                  </button>
                </div>
              </div>
            </Section>
          )}

          {list.length === 0 && !showForm && (
            <Section title="Cargos">
              <p style={{ color: 'var(--faint)', textAlign: 'center', padding: '2rem 0' }}>
                Nenhum cargo cadastrado. Crie o primeiro para vincular pessoas.
              </p>
            </Section>
          )}

          {list.map((raw) => {
            const fn = asRecord(raw);
            const count = asRecord(fn._count);
            return (
              <div
                key={str(fn.id)}
                onClick={() => {
                  void navigate(`/cargos/${String(fn.id)}`);
                }}
                style={{
                  background: '#fff',
                  borderRadius: 14,
                  border: '1px solid var(--line)',
                  padding: '1rem 1.25rem',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{str(fn.name)}</span>
                      <Badge label={labelOf(TYPE_LABELS, fn.type)} variant="info" />
                      <Badge
                        label={labelOf(STATUS_LABELS, fn.status)}
                        variant={str(fn.status) === 'ACTIVE' ? 'success' : 'default'}
                      />
                      {fn.isSensitive ? <Badge label="Sensível" variant="warning" /> : null}
                    </div>
                    <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0.25rem 0 0' }}>
                      {str(fn.description, 'Sem descrição')}
                    </p>
                  </div>
                  <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                    {Number(count.assignments ?? 0)} atribuições
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const inputCss: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--line)',
  borderRadius: 10,
  fontSize: '0.875rem',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};
const primaryBtn: React.CSSProperties = {
  background: 'var(--green)',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '0.5rem 1rem',
  fontSize: '0.85rem',
  cursor: 'pointer',
  fontWeight: 600,
};
const secondaryBtn: React.CSSProperties = {
  background: '#fff',
  color: 'var(--muted)',
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '0.5rem 1rem',
  fontSize: '0.85rem',
  cursor: 'pointer',
};
